import { useMemo, useState } from 'react';
import type { LatLng, LobbyState, WSClientMsg } from '@geoguess/shared';
import { Check, MapPin } from 'lucide-react';
import type { AuthedUser } from '../discord.js';
import { Pano } from '../components/Pano.js';
import { GuessMap } from '../components/GuessMap.js';
import { Timer } from '../components/Timer.js';
import { PlayerAvatar } from '../components/PlayerAvatar.js';
import { Spinner } from '../components/Spinner.js';

export function RoundScreen({
  lobby,
  me,
  send,
}: {
  lobby: LobbyState;
  me: AuthedUser;
  send: (msg: WSClientMsg) => void;
}) {
  const [guess, setGuess] = useState<LatLng | null>(null);
  const submitted = !!lobby.guessed[me.userId];

  const players = useMemo(() => Object.values(lobby.players), [lobby.players]);

  if (!lobby.currentImage || !lobby.roundDeadline || !lobby.roundStartedAt) {
    return (
      <div className="bg-hero grid h-full place-items-center text-muted">
        <div className="flex items-center gap-3">
          <Spinner size={20} />
          <span className="text-sm">Lädt Runde…</span>
        </div>
      </div>
    );
  }

  const totalMs = (lobby.roundDeadline - lobby.roundStartedAt) || lobby.roundTimeSec * 1000;

  const submit = () => {
    if (!guess || submitted) return;
    send({ t: 'guess', lat: guess.lat, lng: guess.lng });
  };

  return (
    <div className="grid h-full grid-rows-[1fr_auto] gap-2 overflow-hidden bg-bg p-2">
      <div className="grid h-full min-h-0 grid-cols-[7fr_3fr] gap-2 overflow-hidden">
        {/* ── Pano ──────────────────────────────────────────── */}
        <div className="relative overflow-hidden rounded-2xl bg-black">
          <Pano image={lobby.currentImage} />
          <div className="pointer-events-none absolute left-3 top-3 inline-flex items-center gap-2 rounded-full bg-black/55 px-3 py-1.5 text-[12px] font-medium text-fg backdrop-blur-md ring-1 ring-white/10">
            <span className="text-muted">Runde</span>
            <span className="font-semibold tabular-nums">
              {lobby.currentRound + 1}/{lobby.totalRounds}
            </span>
            {lobby.region && (
              <>
                <span className="h-3 w-px bg-white/15" />
                <span>{lobby.region.emoji}</span>
                <span className="text-muted">{lobby.region.name}</span>
              </>
            )}
          </div>
        </div>

        {/* ── Right column: map + CTA ───────────────────────── */}
        <div className="grid grid-rows-[1fr_auto] gap-2 overflow-hidden">
          <div className="overflow-hidden rounded-2xl ring-1 ring-border">
            <GuessMap guess={guess} onPick={setGuess} disabled={submitted} />
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={!guess || submitted}
            className={`press inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3.5 text-[15px] font-semibold transition ${
              submitted
                ? 'bg-success/15 text-success'
                : guess
                ? 'bg-accent text-white shadow-[0_8px_24px_rgba(10,132,255,0.35)] hover:brightness-110'
                : 'bg-panel-2 text-muted-2'
            } disabled:cursor-not-allowed`}
          >
            {submitted ? (
              <>
                <Check className="h-4 w-4" />
                Tipp abgegeben
              </>
            ) : guess ? (
              <>
                <MapPin className="h-4 w-4" />
                Tipp bestätigen
              </>
            ) : (
              <span>Tippe auf die Karte</span>
            )}
          </button>
        </div>
      </div>

      {/* ── Footer bar: timer + who guessed ───────────────── */}
      <div className="flex items-center gap-4 rounded-2xl bg-panel px-4 py-3">
        <div className="flex-1">
          <Timer deadline={lobby.roundDeadline} totalMs={totalMs} />
        </div>
        <div className="flex items-center gap-1.5">
          {players.map((p) => (
            <div key={p.userId} title={p.name}>
              <PlayerAvatar
                name={p.name}
                url={p.avatar || undefined}
                size={30}
                online={!!lobby.guessed[p.userId]}
                dim={!lobby.guessed[p.userId]}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
